// Enterprise standard module sidebar (desktop) + mobile pill nav — see /app/memory/DESIGN_SYSTEM.md §16
// UX-2.5A: the module name is asserted ONCE, by the global header chip. This
// component renders plain navigation links only — no heading, no brand card,
// no tagline, no gradient. `title` survives as the accessible nav name.
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';

const itemClass = ({ isActive }) =>
  cn(
    'relative flex items-center gap-2.5 rounded-md px-3 min-h-[36px] py-2 text-sm transition-colors',
    isActive
      ? 'bg-teal-50 text-teal-900 font-semibold shadow-[inset_0_0_0_1px_rgb(153_246_228_/_0.9)] before:absolute before:left-0 before:top-1 before:bottom-1 before:w-[3px] before:rounded-r before:bg-teal-700'
      : 'text-zinc-700 hover:bg-zinc-200/70 hover:text-zinc-900'
  );

const pillClass = ({ isActive }) =>
  cn(
    'shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 min-h-[34px] py-1.5 text-[13px] font-medium transition-colors',
    isActive ? 'bg-teal-700 text-white border-teal-700' : 'bg-white text-zinc-600 border-zinc-200 active:bg-zinc-50'
  );

// Shared exports so custom layouts (Settings/Planner/Mfg) use IDENTICAL nav states.
export const sidebarItemClass = itemClass;
export const sidebarPillClass = pillClass;

export const ModuleSidebar = ({ title, sections, testid, actions }) => {
  const location = useLocation();
  const allItems = sections.flatMap((s) => s.items);
  const cls = (it, base) => (state) =>
    base({ isActive: it.activeWhen ? it.activeWhen(location) : state.isActive });

  return (
    <aside className="md:w-64 shrink-0" data-testid={testid}>
      {/* Mobile: horizontal pill nav */}
      <nav aria-label={title}
        className="md:hidden flex items-center gap-1.5 overflow-x-auto pb-0.5"
        data-testid={testid ? `${testid}-mobile` : undefined}>
        {allItems.map((it) => (
          <NavLink key={it.to} to={it.to} end={it.end} className={cls(it, pillClass)}
            data-testid={it.testid ? `${it.testid}-m` : undefined}>
            {it.icon && <it.icon size={15} weight="duotone" />} {it.label}
          </NavLink>
        ))}
      </nav>

      {/* Desktop */}
      <div className="hidden md:block md:sticky md:top-24 space-y-3">
        {actions && <div className="px-1">{actions}</div>}
        <nav className="space-y-4" aria-label={title}>
          {sections.map((s, i) => (
            <div key={s.label || i} className="space-y-0.5">
              {s.label && <p className="px-3 pb-1 text-xs font-semibold text-zinc-500">{s.label}</p>}
              {s.items.map((it) => (
                <NavLink key={it.to} to={it.to} end={it.end} className={cls(it, itemClass)}
                  data-testid={it.testid}>
                  {it.icon && <it.icon size={17} weight="duotone" />} {it.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
      </div>
    </aside>
  );
};
